import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useVenue } from "../VenueProvider";
import { useAuth } from "../AuthProvider";
import { esSoloLectura } from "../../utils/roles";
import { loadProveedorCatalogs } from "../../utils/catalogs";
import { Badge } from "../badge";
import { normalizeDepositoEstado } from "../../utils/depositoEstado";
import { categoriasDe, tieneCategoria, tieneSubcategoria, esPlannerProveedor, daServicios } from "../../utils/proveedores";
import ProveedorCreateDialog from "../ProveedorCreateDialog";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  StarIcon,
  UserGroupIcon,
  GlobeAltIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

const STATUS_LABELS = {
  activo: { label: "Activo", color: "emerald" },
  inactivo: { label: "Inactivo", color: "zinc" },
  bloqueado: { label: "Bloqueado", color: "red" },
};

const InstagramIcon = ({ className = "h-4 w-4" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const ensureUrlProtocol = (url) => {
  if (!url) return "";
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

// `filtroTipo`: "proveedores" (sin planners) | "planners" (solo planners) |
// "todos". El Directorio (FRA-353) reparte las tres entidades en pestañas.
const ProveedoresDirectorio = ({ filtroTipo = "todos" }) => {
  const navigate = useNavigate();
  const {
    proveedores,
    proveedoresLoading,
    selectedVenueId,
    events,
    loadProveedorDepositos,
  } = useVenue();
  const [depositosPorProveedor, setDepositosPorProveedor] = useState({});

  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const { userInfo } = useAuth();
  // Inversionista (P-FRA-13): consulta el directorio sin dar de alta.
  const soloLectura = esSoloLectura(userInfo);
  const [search, setSearch] = useState("");
  const [filterCategoria, setFilterCategoria] = useState("");
  const [filterSubcategoria, setFilterSubcategoria] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Depósitos en custodia por proveedor. Antes esto era una pestaña entera
  // para un número por proveedor; como columna se ve en el mismo golpe de
  // vista que el resto del directorio.
  useEffect(() => {
    let cancelled = false;
    if (!selectedVenueId || !proveedores.length) {
      setDepositosPorProveedor({});
      return;
    }
    loadProveedorDepositos()
      .then((deps) => {
        if (cancelled) return;
        const map = {};
        (deps || []).forEach((d) => {
          const estado = normalizeDepositoEstado(d.estado);
          if (estado !== "activo") return;
          map[d.proveedorId] = (map[d.proveedorId] || 0) + (Number(d.monto) || 0);
        });
        setDepositosPorProveedor(map);
      })
      .catch((e) => console.warn("No se pudieron cargar los depósitos:", e));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVenueId, proveedores.length]);

  useEffect(() => {
    let cancelled = false;
    loadProveedorCatalogs()
      .then((data) => {
        if (cancelled) return;
        setCategorias(data.categorias);
        setSubcategorias(data.subcategorias);
      })
      .catch((e) => console.error("Error loading catalogs:", e));
    return () => { cancelled = true; };
  }, []);

  const categoriaNameById = useMemo(() => {
    const m = {};
    categorias.forEach((c) => { m[c.id] = c.nombre; });
    return m;
  }, [categorias]);

  const subcategoriaNameById = useMemo(() => {
    const m = {};
    subcategorias.forEach((s) => { m[s.id] = s.nombre; });
    return m;
  }, [subcategorias]);

  const eventCountByProveedor = useMemo(() => {
    const counts = {};
    (events || []).forEach((e) => {
      if (!Array.isArray(e.proveedoresAsignados)) return;
      e.proveedoresAsignados.forEach((a) => {
        if (!a?.proveedorId) return;
        counts[a.proveedorId] = (counts[a.proveedorId] || 0) + 1;
      });
    });
    return counts;
  }, [events]);

  const subcategoriasForFilter = useMemo(
    () =>
      filterCategoria
        ? subcategorias.filter((s) => s.parentId === filterCategoria)
        : [],
    [subcategorias, filterCategoria]
  );

  // Un planner que además da servicios aparece en las DOS pestañas: es
  // planner por su comisión y proveedor por lo que renta.
  const porTipo = useMemo(
    () =>
      filtroTipo === "planners"
        ? proveedores.filter(esPlannerProveedor)
        : filtroTipo === "proveedores"
        ? proveedores.filter(daServicios)
        : proveedores,
    [proveedores, filtroTipo]
  );

  // Status counts for chip filter (excludes archived from non-archived buckets)
  const statusCounts = useMemo(() => {
    const c = { activo: 0, inactivo: 0, bloqueado: 0, total: 0, archived: 0 };
    porTipo.forEach((p) => {
      if (p.archivedAt) { c.archived += 1; return; }
      c.total += 1;
      const s = p.status || "activo";
      if (c[s] != null) c[s] += 1;
    });
    return c;
  }, [porTipo]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return porTipo.filter((p) => {
      if (term && !(p.nombre || "").toLowerCase().includes(term)) return false;
      // Un proveedor puede estar en varias categorías: el filtro acierta
      // con cualquiera de ellas, no solo con la principal.
      if (filterCategoria && !tieneCategoria(p, filterCategoria)) return false;
      if (filterSubcategoria && !tieneSubcategoria(p, filterSubcategoria)) return false;
      if (filterStatus === "archived") {
        if (!p.archivedAt) return false;
      } else {
        if (p.archivedAt) return false;
        if (filterStatus && (p.status || "activo") !== filterStatus) return false;
      }
      return true;
    });
  }, [porTipo, search, filterCategoria, filterSubcategoria, filterStatus]);

  const sorted = useMemo(
    () =>
      [...filtered].sort((a, b) =>
        (a.nombre || "").localeCompare(b.nombre || "")
      ),
    [filtered]
  );

  if (!selectedVenueId) {
    return (
      <div className="p-8 text-center text-zinc-500">
        Selecciona un venue para ver sus proveedores.
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Toolbar: search + categoría + subcategoría + Nuevo */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre..."
            className="w-full rounded-lg border border-zinc-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
          />
        </div>
        {filtroTipo !== "planners" && (
        <select
          value={filterCategoria}
          onChange={(e) => {
            setFilterCategoria(e.target.value);
            setFilterSubcategoria("");
          }}
          className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
        >
          <option value="">Todas las categorías</option>
          {categorias.map((c) => (
            <option key={c.id} value={c.id}>{c.nombre}</option>
          ))}
        </select>
        )}
        {filtroTipo !== "planners" && (
        <select
          value={filterSubcategoria}
          onChange={(e) => setFilterSubcategoria(e.target.value)}
          disabled={!filterCategoria}
          className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e] disabled:opacity-50"
        >
          <option value="">Todas las subcategorías</option>
          {subcategoriasForFilter.map((s) => (
            <option key={s.id} value={s.id}>{s.nombre}</option>
          ))}
        </select>
        )}
        {!soloLectura && (
        <button
          onClick={() => setCreateOpen(true)}
          className="ml-auto px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 flex items-center gap-1.5"
          style={{ backgroundColor: ACCENT }}
        >
          <PlusIcon className="h-4 w-4" />
          {filtroTipo === "planners" ? "Nuevo planner" : "Nuevo proveedor"}
        </button>
        )}
      </div>

      {/* Estado chips */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-zinc-500 mr-1">Mostrar:</span>
        {[
          { value: "", label: "Todos", count: statusCounts.total, activeBg: "#52525B" },
          { value: "activo", label: "Activos", count: statusCounts.activo, activeBg: "#059669" },
          { value: "inactivo", label: "Inactivos", count: statusCounts.inactivo, activeBg: "#52525B" },
          { value: "bloqueado", label: "Bloqueados", count: statusCounts.bloqueado, activeBg: "#DC2626" },
          { value: "archived", label: "Archivados", count: statusCounts.archived, activeBg: "#71717A" },
        ].map((opt) => {
          const active = filterStatus === opt.value;
          return (
            <button
              key={opt.value || "todos"}
              type="button"
              onClick={() => setFilterStatus(opt.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                active
                  ? "text-white shadow-sm"
                  : "text-zinc-600 bg-zinc-100 hover:bg-zinc-200"
              }`}
              style={active ? { backgroundColor: opt.activeBg } : undefined}
            >
              {opt.label}
              <span className={`ml-1.5 ${active ? "opacity-90" : "text-zinc-400"}`}>
                ({opt.count})
              </span>
            </button>
          );
        })}
      </div>

      {/* Table */}
      {proveedoresLoading ? (
        <div className="text-center py-12 text-zinc-400">Cargando...</div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-16 px-4">
          <UserGroupIcon className="h-10 w-10 mx-auto text-zinc-300 mb-3" />
          <div className="text-zinc-500">
            {porTipo.length === 0
              ? filtroTipo === "planners"
                ? "Aún no hay planners. Crea el primero."
                : "Aún no hay proveedores. Crea el primero."
              : "Nadie coincide con los filtros."}
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700/60">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-50 dark:bg-zinc-800/40">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Nombre</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Categoría</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Contactos</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Enlaces</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Eventos</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">En custodia</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Rating</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
              {sorted.map((p) => {
                const status = STATUS_LABELS[p.status || "activo"] || STATUS_LABELS.activo;
                const cats = categoriasDe(p).map((e) => ({
                  key: e.categoria,
                  cat: categoriaNameById[e.categoria] || e.categoria,
                  sub: e.subcategoria ? subcategoriaNameById[e.subcategoria] : null,
                }));
                return (
                  <tr
                    key={p.id}
                    onClick={() => navigate(`/dashboard/proveedores/${p.id}`)}
                    className="cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition-colors"
                  >
                    <td className="px-4 py-2.5 font-medium text-zinc-900 dark:text-white">
                      <div className="flex items-center gap-2">
                        {p.nombre}
                        {p.isPlanner && <Badge color="indigo">Planner</Badge>}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-zinc-700 dark:text-zinc-300">
                      {cats.length > 0 ? (
                        <div className="space-y-1">
                          {cats.map((c) => (
                            <div key={c.key} className="leading-tight">
                              <div>{c.cat}</div>
                              {c.sub && (
                                <div className="text-xs text-zinc-400">{c.sub}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-zinc-300 italic">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-zinc-500">
                      {(p.contactos || []).length || <span className="text-zinc-300 italic">0</span>}
                    </td>
                    <td className="px-4 py-2.5 text-zinc-500">
                      <div className="flex items-center gap-2">
                        {p.instagram ? (
                          <a
                            href={`https://instagram.com/${p.instagram}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title={`@${p.instagram}`}
                            className="text-pink-500 hover:text-pink-600 transition-colors"
                          >
                            <InstagramIcon className="h-4 w-4" />
                          </a>
                        ) : (
                          <span className="text-zinc-200"><InstagramIcon className="h-4 w-4" /></span>
                        )}
                        {p.website ? (
                          <a
                            href={ensureUrlProtocol(p.website)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title={p.website}
                            className="text-blue-500 hover:text-blue-600 transition-colors"
                          >
                            <GlobeAltIcon className="h-4 w-4" />
                          </a>
                        ) : (
                          <span className="text-zinc-200"><GlobeAltIcon className="h-4 w-4" /></span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-zinc-500">
                      {eventCountByProveedor[p.id] || <span className="text-zinc-300 italic">0</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      {depositosPorProveedor[p.id] > 0 ? (
                        <span className="font-medium text-amber-600">
                          ${depositosPorProveedor[p.id].toLocaleString("es-MX")}
                        </span>
                      ) : (
                        <span className="text-zinc-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-zinc-700 dark:text-zinc-300">
                      {p.ratingCount > 0 ? (
                        <span className="inline-flex items-center gap-1">
                          <StarIcon className="h-4 w-4 text-amber-400" />
                          <span className="font-medium">
                            {Number(p.ratingPromedio).toFixed(1)}
                          </span>
                          <span className="text-xs text-zinc-400">
                            ({p.ratingCount})
                          </span>
                        </span>
                      ) : (
                        <span className="text-zinc-300 italic">Sin reseñas</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge color={status.color}>{status.label}</Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!soloLectura && (
        <ProveedorCreateDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          initialCategoria={filtroTipo === "planners" ? "planner" : ""}
          onCreated={(id) => navigate(`/dashboard/proveedores/${id}`)}
        />
      )}
    </div>
  );
};

export default ProveedoresDirectorio;
